import React, { Component } from 'react';
import { connect } from 'react-redux';
import Sizeable from "react-sizeable";
import { createSelector, createStructuredSelector } from "reselect";
import Transition from "join-transition";
import Classnames from 'classnames';
import { scaleLinear, isoParse, timeFormat } from 'd3'

import monteCarlo from '../actions/query-api/monte-carlo'
import { retrievePDF } from "../actions/save-to-pdf";

import Timeline from "../Timeline";
import Radio from "../components/Radio";
import EditableName from '../components/EditableName'
import Column from '../components/Column'
import Slider from '../components/Column/Slider.js'

import { format } from "d3-format"

import './style.css'

const calculateMonteCarloValue = (indexes, key, yrs, preIn, postOut, principal) => {
  let arr = [indexes[0][key] * (principal/10e5)]
  for(let i = 1; i < yrs + 1; i++){
    arr.push(arr[i-1] * (indexes[i][key]/indexes[i-1][key]) + preIn)
  }
  for(let i = yrs + 1; i < indexes.length; i++){
    arr.push(Math.max(0, arr[i-1] * (indexes[i][key]/indexes[i-1][key]) - postOut))
  }
  return arr;
}

const dataSelector = createSelector(
  [
    (state, props) => state.params,
    (state, props) => props.data,
    (state, props) => state.yBandVars
  ],
  (params, data, yBandVars) => {
    const [original, proposed] = ["Original", "Proposed"].map(side => {
      let sideData = data
        .filter(d => d.type_tag === side)
      sideData = sideData
        .slice(0, Math.min(params.yrs.value + 30, sideData.length) + 1)
      const arrs = yBandVars.reduce((t,v) => ({
        ...t,
        [v]: calculateMonteCarloValue(
          sideData,
          v, 
          params.yrs.value, 
          params.preIn.value, 
          params.postOut.value,
          params.principal.value,
        )
      }), {})
      return sideData.map((d,i) => ({
        ...d, 
        ...yBandVars.reduce((t,v) => ({ ...t, ['adjusted_'+v]: arrs[v][i] }), {})
      }))
    })
    return [...original, ...proposed]
  }
)

class MonteCarlo extends Component {

  constructor(props){
    super(props);
    this.state = {
      hidden: true,
      savedState: true,
      pdfSavedState: true,
      yBandVars: ["monteCarlo5pctUV1M","monteCarlo10pctUV1M","monteCarlo50pctUV1M","monteCarlo90pctUV1M","monteCarlo95pctUV1M"],
      params: {
        principal: {
          label: 'Initial Value',
          value: this.props.initialValue,
          domain: [Math.min(1, this.props.initialValue), 2*Math.max(10e5, this.props.initialValue)]
        },
        yrs: {
          label: this.props.TEXT.yearsTillRetirementLabel,
          value: 0,
          domain: [0, 60],
        },
        preIn: {
          label: this.props.TEXT.annualInvestmentLabel,
          value: 0,
          domain: [0, 5*10e4],
        },
        postOut: {
          label: this.props.TEXT.retirementIncomeLabel,
          value: 0,
          domain: [0, 5*10e4],
        },
      }
    }
    this.onParamChange = this.onParamChange.bind(this)
    this.onMenuSelect = this.onMenuSelect.bind(this)
    this.onSave = this.onSave.bind(this)
    this.onPdfSave = this.onPdfSave.bind(this)
  }

  componentWillReceiveProps(nextProps){
    const { selected, sims, open } = nextProps;
    if((!sims[selected].needsSaving || sims[selected].permanent) !== this.state.savedState){    
      this.setState({ savedState: !sims[selected].needsSaving || sims[selected].permanent })
    }
    if(open && !this.props.open){ // opening
      this.setState({ hidden: false })
    } else if (!open && this.props.open) { // closing
      setTimeout(() => {
        this.setState({ hidden: true })
      },500)
    }
  }

  onMenuSelect(index){
    let template = this.props.sims[index]

    // don't retain unsaved sims when a new test is selected
    // maybe find a better way to do this
    if(index > 0 && this.props.sims[0].needsSaving){ 
      this.props.deleteSim(null, 0);
      index--;
    }

    if(template.permanent){
      let name = template.name === "New scenario" 
        ? "Unnamed new scenario"
        : "Unnamed scenario ("+template.name+")"
      this.props.addSim({ ...template, name, id: null })
      this.props.selectSim(0)
    } else {
      this.props.selectSim(index)
    }
  }

  onParamChange(k, value){
    this.setState({
      pdfSavedState: false,
      params: {
        ...this.state.params,
        [k]: {
          ...this.state.params[k],
          value: Math.round(value)
        }
      }
    })
  }

  onSave(){
    this.props.saveSims(this.props.sims[this.props.selected])
    // retrievePDF is called in callback
  }

  onPdfSave(){
    this.setState({
      pdfSavedState: true
    })
    this.props.retrievePDF()
  }

  render(){
    const { 
      monteCarloSplit,
      portfolios,
      versions,
      colorScale,
      reportWidth,
      columns,
      allColumns,
      sims,
      selected,
      open,
      TEXT,
      columnLabels,
      TEST_INFO,
      currentMonteCarloAssumptions,
      originalPortfolioLabel,
      proposedPortfolioLabel,
      originalPortfolioShortLabel,
    } = this.props
    const { setOpen, deleteSim, resetFactors, defaultFactors, setProp, setFactor } = this.props
    const { hidden, savedState, pdfSavedState, params, yBandVars } = this.state

    const menu = sims.map(({ name, title, permanent, pending, flagFake }, i) => ({ value: i, label: name, permanent, pending, flagFake }));

    const mcData = dataSelector(this.state, this.props)
    const dates = [
      timeFormat("%m/%d/%Y")(isoParse(mcData[0].date)), 
      timeFormat("%m/%d/%Y")(isoParse(mcData[ mcData.length - 1 ].date))
    ]

    return (<div className="MonteCarlo">
      <div>
        <h4 className="narrow">
          {TEXT.hypotheticalGrowthOf1MProjectedLabel}
          <small>
            {` (${dates[0]} to ${dates[1]})`}
          </small>
        </h4>

        <div className="params">
          {Object.keys(params).map(k => 
            <Slider nominmax
              width={200} //hard coded elsewhere
              key={'param-'+k}
              factor={k}
              label={params[k].label}
              value={params[k].value}
              format={k === 'yrs' ? ".0f" : "$,.0f"}
              scale={scaleLinear().domain(params[k].domain).range([0, 200])}
              onChange={this.onParamChange}/>)}
          <div 
            className={Classnames("toggle-open-close", { closed: !open })}
            onClick={() => setOpen(!open) }>
            <span className="click-to">
              {open ? TEXT.clickToCollapseScenarioLabel : TEXT.clickToExpandScenarioLabel}
            </span>
          </div>
          {!open && <div className="button-container button-default horizontal">
            <button 
              className={pdfSavedState ? "saved" : "unsaved"}
              onClick={this.onPdfSave}>
              RUN PDF
            </button>
          </div>}
        </div>

        <div className={Classnames("collapsable", { closed: !open, hidden })}>
          <div className="control">
            <div className="editable-name-container horizontal">
              <EditableName 
                TEXT={TEXT}
                value={sims[selected].name} 
                onChange={value => setProp('name', value)} 
                version="name" 
                displayName={
                  <Radio
                    type="dropdown"
                    values={menu}
                    selected={menu[selected]}
                    onChange={this.onMenuSelect}
                    deleteCallback={index => deleteSim(sims[index].id, index)} />
                }/>
            </div>
            <div className="small-button-container button-reset horizontal">
              <button onClick={() => resetFactors()}>
                {TEXT.resetButton}
              </button>
            </div>
            <div className="small-button-container button-default horizontal">
              <button onClick={() => defaultFactors()}> 
                {TEXT.defaultButton}
              </button>
            </div>
            <div className="button-container horizontal">
              <button 
                className={savedState ? "saved" : "unsaved"}
                onClick={this.onSave}>
                <span className="not-mobile">{savedState ? TEXT.savedButton : TEXT.saveRunPDFButton}</span>
                <span className="mobile">{savedState ? TEXT.savedButton : TEXT.saveRunPDFMobileButton}</span>
              </button>
            </div>
          </div>
          <div>
            <textarea 
              className="mobile-hide"
              cols={12}
              placeholder={TEXT.addDescriptionLabel}
              value={sims[selected].description 
                ? sims[selected].description 
                : undefined}
              onBlur={event => setProp('description', event.target.value)} />
            <div className="monte-carlo-controls">
              <div className="column-holder">
                {Object.keys(columns).map(c => 
                  <Column 
                    key={c}
                    label={TEXT[columnLabels[c]]}
                    data={columns[c]}
                    //rates={c === "rates" && rates}
                    portfolios={portfolios}
                    factors={sims[selected].expectedReturnAssumptions}
                    volatilityAssumptions={sims[selected].expectedVolatilityAssumptions}
                    onChange={(factor, value, assumptions) => setFactor(factor, value, assumptions)} 
                  />)}
              </div>
              <div className="guidance">
                <span className="growth-explanation-gp-replace">{TEXT.growthExplanation}</span>
              </div>
            </div>
          </div>
        </div>
        <Transition values={+monteCarloSplit} duration={600}>
          {monteCarloSplit =>
            <Sizeable>
              <Timeline
                data={mcData}
                before={versions.original}
                after={versions.proposed}
                x="dateDelta"
                y={yBandVars.map(v => 'adjusted_'+v)}
                baseline={0}
                split={monteCarloSplit}
                colorScale={colorScale}
                xFormat="%y yrs"
                yFormat="2s"
                detailFormat="$,.0f"
                isVertical={false}
                reportWidth={reportWidth}
                yAxisRight={true}
                margin={{ left: 0 }}
                retirement={mcData[params.yrs.value]}
                originalPortfolioLabel={originalPortfolioLabel}
                proposedPortfolioLabel={proposedPortfolioLabel}
                originalPortfolioShortLabel={originalPortfolioShortLabel}
              />
            </Sizeable>}
        </Transition>
      </div>
      
      <div className="compliance">{TEXT.projectedGrowthERDisclosure}
        {Object.entries(currentMonteCarloAssumptions.expectedReturnAssumptions)
          .filter(d => d[0] !== 'date' && !~Object.keys(columns.rates).indexOf(d[0]) && !!d[1])
          .reduce((t,v) => 
            t+` ${allColumns[v[0]]}: ${format(".2%")(v[1])},`
          , '') +
        Object.entries(currentMonteCarloAssumptions.expectedReturnAssumptions)
          .filter(d => !!~Object.keys(columns.rates).indexOf(d[0]) && !!d[1])
          .reduce((t,v) =>
            t+` ${allColumns[v[0]]}: ${format(".2%")(v[1])},`
          , '')
        .slice(0,-1) + '.'}
      </div>
      <br/>
      <div className="compliance">{TEXT.projectedGrowthVolDisclosure}
        {Object.entries(currentMonteCarloAssumptions.expectedVolatilityAssumptions)
          .filter(d => d[0] !== 'date' && !~Object.keys(columns.rates).indexOf(d[0]) && !!d[1])
          .reduce((t,v) => 
            t+` ${allColumns[v[0]]}: ${format(".2%")(v[1])},`
          , '') +
        Object.entries(currentMonteCarloAssumptions.expectedVolatilityAssumptions)
          .filter(d => !!~Object.keys(columns.rates).indexOf(d[0]) && !!d[1])
          .reduce((t,v) => 
            t+` ${allColumns[v[0]]}: ${format(".2%")(v[1])},`
          , '')
        .slice(0,-1) + '.'}
      </div>
      <br/>
      <div className="compliance growth-disclosure-gp-replace" 
        dangerouslySetInnerHTML={{__html: TEXT.growthDisclosure.replace(/\[\[LINK\]\]/g, TEST_INFO)}}></div>
    </div>)
  }

}

const mapStateToProps = createStructuredSelector({
  monteCarloSplit: state => state.monteCarloSplit,
  columns: state => state.customStressTestColumns,
  allColumns: state => ({
    ...state.customStressTestColumns.equity,
    ...state.customStressTestColumns.rates,
    ...state.customStressTestColumns.other
  }),
  sims: state => state.monteCarlos,
  selected: state => state.selectedMonteCarlo,
  rates: state => state.riskFreeRates,
  open: state => state.monteCarloIsOpen,
  TEXT: state => state.complianceText,
  columnLabels: state => state.customStressTestColumnLabelsLookup,
  initialValue: state => state.analysis[0].totalValue,
  currentMonteCarloAssumptions: state => state.analysis[0].monteCarlos[state.analysis[0].monteCarlos.length - 1]
});

const mapDispatchToProps = dispatch => ({
  retrievePDF: () => dispatch(retrievePDF),
  setOpen: open => dispatch({ type: 'SET_MONTE_CARLO_OPEN', open }),
  addSim: sim => dispatch({ type: "ADD_MONTE_CARLO", sim }),
  selectSim: index => dispatch({ type: "SELECT_MONTE_CARLO", index }),
  deleteSim: (id, index) => {
    if(!id){
      dispatch({ type: 'DELETE_MONTE_CARLO', index })
    } else {
      dispatch(monteCarlo("PUT", { id }, null, true))
    }
  },
  resetFactors: () => dispatch({ type: 'RESET_MONTE_CARLO_FACTORS' }),
  defaultFactors: () => dispatch({ type: 'SET_DEFAULT_MONTE_CARLO_FACTORS' }),
  setProp: (prop, value) => dispatch({ type: 'SET_MONTE_CARLO_PROP', prop, value }),
  saveSims: sim => {
    if(!!sim.id){
      dispatch(monteCarlo('PUT', sim, true))
    } else {
      dispatch(monteCarlo('POST', sim, false))
    }
  },
  setFactor: (factor, value, assumptions) => 
    dispatch({ type: 'SET_MONTE_CARLO_FACTOR', factor, value, assumptions }),
});

export default connect(mapStateToProps, mapDispatchToProps)(MonteCarlo);


// WEBPACK FOOTER //
// ./src/MonteCarlo/index.js